import axios from "axios";
import { ArrowLeft, Eye, EyeOff, Lock, Mail, Shield, User } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../../assets/css/admin/SubadminForm.css";
import AppContext from "../../../context/Appcontext";

const SubAdminForm = () => {
  const navigate = useNavigate();
  const { baseUrl } = useContext(AppContext);
  const [id, setId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [subAdmins, setSubAdmins] = useState([]);

  const createSubAdmin = () => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("firstName", firstName);
    formData.append("lastName", lastName);
    formData.append("email", email);
    formData.append("password", password);
    formData.append("is_superadmin", isSuperAdmin ? 1 : 0);
    axios
      .post(`${baseUrl}/admin/auth/createSubAdmin`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        console.log(response.data);
        viewAllSubAdmin();
        resetForm();
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const updateSubAdmin = () => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("id", id);
    formData.append("firstName", firstName);
    formData.append("lastName", lastName);
    formData.append("email", email);
    if (password) formData.append("password", password);
    formData.append("is_superadmin", isSuperAdmin);
    axios
      .post(`${baseUrl}/admin/auth/updateSubAdmin`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        console.log(response.data);
        viewAllSubAdmin();
        resetForm();
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const deleteSubAdmin = (id) => {
    axios
      .post(`${baseUrl}/admin/auth/deleteSubAdmin`, { id })
      .then(() => viewAllSubAdmin())
      .catch((error) => console.log(error));
  };

  const viewAllSubAdmin = () => {
    axios
      .post(`${baseUrl}/admin/auth/viewAllSubAdmin`)
      .then((response) => {
        console.log(response.data);
        setSubAdmins(response.data.subAdmins);
      })
      .catch((error) => console.log(error));
  };

  const resetForm = () => {
    setId("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setIsSuperAdmin(false);
    setEditId(null);
  };

  const handleEdit = (admin) => {
    setEditId(admin.id);
    setId(admin.id);
    setFirstName(admin.firstName);
    setLastName(admin.lastName);
    setEmail(admin.email);
    setPassword("");
    setIsSuperAdmin(admin.is_superadmin);
  };

  const handleSubmit = () => {
    if (editId) updateSubAdmin();
    else createSubAdmin();
  };

  useEffect(() => {
    viewAllSubAdmin();
  }, []);

  return (
    <div className="saf-page">
      {/* ── Page Header ── */}
      <div className="saf-page-header">
        <div className="saf-page-badge">
          <Shield size={12} />
          ADMIN PANEL
        </div>
        <h1 className="saf-page-title">
          {editId ? "Edit Sub Admin" : "Create Sub Admin"}
        </h1>
        <p className="saf-page-subtitle">
          Set up a new administrative account with role-based access.
        </p>
      </div>

      {/* ── Main Card ── */}
      <div className="saf-card">
        {/* Two-column layout: form left, role+footer right */}
        <div className="saf-card-body">

          {/* LEFT — Identity Fields */}
          <div className="saf-left">
            <div className="saf-section-heading">
              <User size={14} />
              <span>IDENTITY DETAILS</span>
              <div className="saf-section-divider"></div>
            </div>

            {/* Row: First + Last name */}
            <div className="saf-row">
              <div className="saf-field-group">
                <label className="saf-field-label">First Name</label>
                <div className="saf-input-wrapper">
                  <User size={18} className="saf-input-icon" />
                  <input
                    type="text"
                    className="saf-input"
                    placeholder="e.g. Sarah"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
              </div>

              <div className="saf-field-group">
                <label className="saf-field-label">Last Name</label>
                <div className="saf-input-wrapper">
                  <User size={18} className="saf-input-icon" />
                  <input
                    type="text"
                    className="saf-input"
                    placeholder="e.g. Johnson"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Row: Email + Password */}
            <div className="saf-row">
              <div className="saf-field-group">
                <label className="saf-field-label">Email Address</label>
                <div className="saf-input-wrapper">
                  <Mail size={18} className="saf-input-icon" />
                  <input
                    type="email"
                    className="saf-input"
                    placeholder="admin@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="saf-field-group">
                <label className="saf-field-label">Password</label>
                <div className="saf-input-wrapper">
                  <Lock size={18} className="saf-input-icon" />
                  <input
                    type={showPassword ? "text" : "password"}
                    className="saf-input saf-input--has-action"
                    placeholder="Minimum 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="saf-pw-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                </div>
                {password && (
                  <div className="saf-password-hint">Min. 8 characters</div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT — Role + Actions */}
          <div className="saf-right">
            <div className="saf-section-heading">
              <Shield size={14} />
              <span>ROLE ASSIGNMENT</span>
              <div className="saf-section-divider"></div>
            </div>

            <div className="saf-checkbox-wrapper">
              <label className="saf-checkbox-label">
                <input
                  type="checkbox"
                  className="saf-checkbox-input"
                  checked={isSuperAdmin}
                  onChange={(e) => setIsSuperAdmin(e.target.checked)}
                />
                <span className="saf-checkbox-text">Is Super Admin</span>
              </label>
            </div>

            {/* Footer note + buttons sit inside right column */}
            <div className="saf-right-footer">
              <div className="saf-footer-note">
                <Shield size={14} />
                Credentials will be emailed to the admin.
              </div>
              <div className="saf-footer-action-group">
                <button
                  className="saf-btn saf-btn--ghost"
                  onClick={resetForm}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  className="saf-btn saf-btn--primary"
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  {isLoading
                    ? "Processing..."
                    : editId
                      ? "Update Admin"
                      : "Create Admin"}
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── Back Button (below card, right-aligned) ── */}
      <div className="saf-back-row">
        <button className="saf-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} />
          Back to Sub Admin Management
        </button>
      </div>

      {/* ── Sub Admins Table ── */}
      // temp view test
      <div className="saf-subadmin-list">
        <h2 className="saf-subadmin-heading">Existing Sub Admins</h2>
        {subAdmins.length === 0 ? (
          <p>No sub admins found.</p>
        ) : (
          <table className="saf-table">
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Super Admin</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subAdmins.map((admin) => (
                <tr key={admin.id}>
                  <td>{admin.firstName}</td>
                  <td>{admin.lastName}</td>
                  <td>{admin.email}</td>
                  <td>{admin.isSuperAdmin ? "Yes" : "No"}</td>
                  <td>
                    <button
                      className="saf-btn saf-btn--small"
                      onClick={() => handleEdit(admin)}
                    >
                      Edit
                    </button>
                    <button
                      className="saf-btn saf-btn--danger saf-btn--small"
                      onClick={() => deleteSubAdmin(admin.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SubAdminForm;